import React from 'react';
import {withRouter} from "react-router";
import {Link} from "react-router-dom";
import {Button} from "reactstrap";
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import {confirmAlert} from "react-confirm-alert";
import $ from 'jquery';
import APPCONFIG from 'constants/Config';
import KioskSurface from 'components/Parts/Kiosk/KioskSurface';
import {formatApiErrorMessage} from '../../../utils/apiErrorMessage';

const KIOSK_REFRESH_INTERVAL_MS = 5000;
const KIOSK_PAIR_POLL_MS = 2000;
const KIOSK_EXIT_HOLD_MS = 2500;
const KIOSK_PAIR_STORAGE_KEY = 'bestpos_kiosk_pair_session';

function kioskApiUrl(path) {
	return `${APPCONFIG.settings.apiURL()}/${APPCONFIG.settings.apiVersion}${path}`;
}

function readStoredPairSession() {
	try {
		const raw = window.localStorage.getItem(KIOSK_PAIR_STORAGE_KEY);
		if (!raw) {
			return null;
		}
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') {
			return null;
		}
		return parsed;
	} catch (e) {
		return null;
	}
}

function writeStoredPairSession(session) {
	try {
		if (!session) {
			window.localStorage.removeItem(KIOSK_PAIR_STORAGE_KEY);
			return;
		}
		window.localStorage.setItem(KIOSK_PAIR_STORAGE_KEY, JSON.stringify(session));
	} catch (e) {
		// Ignore storage failures (private mode, etc.)
	}
}

class Kiosk extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			error: '',
			loading: false,
			isAuthenticated: false,
			pairing: false,
			pairCode: '',
			pairExpiresAt: null,
			deviceToken: '',
			kioskSession: null,
			exitHoldProgress: 0,
		};
		this.brand = 'POS';
		this.refreshTimer = null;
		this.pairPollTimer = null;
		this.exitHoldTimer = null;
		this.exitHoldStartedAt = null;
		this.exitHoldRaf = null;
	}

	componentDidMount() {
		const stored = readStoredPairSession();
		if (stored && stored.device_token && stored.pkey) {
			this.setState({
				isAuthenticated: true,
				kioskSession: {
					...stored,
					device_token: stored.device_token,
					api_key: stored.device_token || stored.api_key,
				},
				deviceToken: stored.device_token,
			});
			return;
		}
		if (stored && stored.device_token && !stored.pkey) {
			this.setState({
				pairing: true,
				pairCode: stored.pair_code ? String(stored.pair_code).padStart(6, '0') : '',
				pairExpiresAt: stored.pair_code_end_time || null,
				deviceToken: stored.device_token,
			}, () => this.startPairPolling());
			return;
		}
		this.beginPairRequest();
	}

	componentDidUpdate(prevProps, prevState) {
		const wasAuthenticated = prevState.isAuthenticated;
		const isAuthenticated = this.state.isAuthenticated;
		const kioskPkey = this.state.kioskSession && this.state.kioskSession.pkey;
		const deviceToken = this.getDeviceToken(this.state.kioskSession);

		if (!wasAuthenticated && isAuthenticated && kioskPkey && deviceToken) {
			this.stopPairPolling();
			this.startRefreshPolling();
		} else if (wasAuthenticated && !isAuthenticated) {
			this.stopRefreshPolling();
		}
	}

	componentWillUnmount() {
		this.stopRefreshPolling();
		this.stopPairPolling();
		this.clearExitHold();
	}

	startRefreshPolling = () => {
		this.stopRefreshPolling();
		this.refreshTimer = setInterval(() => {
			this.refreshKioskDisplay();
		}, KIOSK_REFRESH_INTERVAL_MS);
	}

	stopRefreshPolling = () => {
		if (this.refreshTimer) {
			clearInterval(this.refreshTimer);
			this.refreshTimer = null;
		}
	}

	startPairPolling = () => {
		this.stopPairPolling();
		this.pairPollTimer = setInterval(() => {
			this.pollPairStatus();
		}, KIOSK_PAIR_POLL_MS);
		this.pollPairStatus();
	}

	stopPairPolling = () => {
		if (this.pairPollTimer) {
			clearInterval(this.pairPollTimer);
			this.pairPollTimer = null;
		}
	}

	clearExitHold = () => {
		if (this.exitHoldTimer) {
			clearTimeout(this.exitHoldTimer);
			this.exitHoldTimer = null;
		}
		if (this.exitHoldRaf) {
			cancelAnimationFrame(this.exitHoldRaf);
			this.exitHoldRaf = null;
		}
		this.exitHoldStartedAt = null;
		if (this.state.exitHoldProgress) {
			this.setState({exitHoldProgress: 0});
		}
	}

	tickExitHoldProgress = () => {
		if (!this.exitHoldStartedAt) {
			return;
		}
		const elapsed = Date.now() - this.exitHoldStartedAt;
		const progress = Math.min(1, elapsed / KIOSK_EXIT_HOLD_MS);
		this.setState({exitHoldProgress: progress});
		if (progress < 1) {
			this.exitHoldRaf = requestAnimationFrame(this.tickExitHoldProgress);
		}
	}

	beginExitHold = (event) => {
		if (event && event.preventDefault) {
			event.preventDefault();
		}
		this.clearExitHold();
		this.exitHoldStartedAt = Date.now();
		this.exitHoldRaf = requestAnimationFrame(this.tickExitHoldProgress);
		this.exitHoldTimer = setTimeout(() => {
			this.clearExitHold();
			this.promptEndKioskSession();
		}, KIOSK_EXIT_HOLD_MS);
	}

	endKioskSession = () => {
		this.stopRefreshPolling();
		this.stopPairPolling();
		writeStoredPairSession(null);
		this.setState({
			isAuthenticated: false,
			pairing: false,
			pairCode: '',
			pairExpiresAt: null,
			deviceToken: '',
			kioskSession: null,
			exitHoldProgress: 0,
			error: '',
		}, () => this.beginPairRequest());
	}

	promptEndKioskSession = () => {
		confirmAlert({
			title: 'End kiosk session?',
			message: 'This will leave customer display mode.',
			buttons: [
				{label: 'Cancel', onClick: () => {}},
				{
					label: 'End Session',
					onClick: () => this.endKioskSession(),
				},
			],
		});
	}

	getDeviceToken = (session) => {
		if (!session) {
			return this.state.deviceToken || '';
		}
		return session.device_token || session.api_key || this.state.deviceToken || '';
	}

	getApiErrorMessage = (error, fallback = 'Unable to pair kiosk.') => {
		const message = formatApiErrorMessage(error, fallback);
		return !message || message === '[object Object]' ? fallback : message;
	}

	unwrapResponse = (data) => {
		if (data && data.response && typeof data.response === 'object') {
			return data.response;
		}
		return data;
	}

	beginPairRequest = async () => {
		this.setState({loading: true, error: '', pairing: true, pairCode: '', deviceToken: ''});
		try {
			const data = await $.ajax({
				type: 'POST',
				url: kioskApiUrl('/kiosks/request_pair'),
				data: {
					object: JSON.stringify({}),
				},
				dataType: 'json',
			});
			const payload = this.unwrapResponse(data);
			const deviceToken = payload && (payload.device_token || payload.api_key);
			const pairCode = payload && payload.pair_code != null
				? String(payload.pair_code).padStart(6, '0')
				: '';
			if (!deviceToken || !pairCode) {
				this.setState({
					loading: false,
					error: 'Unable to start kiosk pairing. Missing code from server.',
				});
				return;
			}

			const session = {
				device_token: deviceToken,
				api_key: deviceToken,
				pair_code: pairCode,
				pair_code_end_time: payload.pair_code_end_time || null,
			};
			writeStoredPairSession(session);

			this.setState({
				loading: false,
				pairing: true,
				pairCode,
				pairExpiresAt: payload.pair_code_end_time || null,
				deviceToken,
				error: '',
			}, () => this.startPairPolling());
		} catch (e) {
			this.setState({
				loading: false,
				error: this.getApiErrorMessage(e, 'Unable to start kiosk pairing.'),
			});
		}
	}

	pollPairStatus = async () => {
		const deviceToken = this.state.deviceToken;
		if (!deviceToken || this.state.isAuthenticated || this.state.loading) {
			return;
		}

		try {
			const data = await $.ajax({
				type: 'POST',
				url: kioskApiUrl('/kiosks/check_pair'),
				data: {
					object: JSON.stringify({
						device_token: deviceToken,
						api_key: deviceToken,
					}),
				},
				dataType: 'json',
			});
			const payload = this.unwrapResponse(data);
			if (!payload) {
				return;
			}

			if (payload.status === 'expired' || payload.status === 'cancelled') {
				this.stopPairPolling();
				writeStoredPairSession(null);
				this.setState({
					error: 'Pair code expired. Generating a new code…',
					pairCode: '',
					deviceToken: '',
				});
				this.beginPairRequest();
				return;
			}

			if (payload.status === 'approved' && payload.pkey) {
				const deviceTokenFinal = payload.device_token || payload.api_key || deviceToken;
				const kioskSession = {
					...payload,
					device_token: deviceTokenFinal,
					api_key: deviceTokenFinal,
				};
				writeStoredPairSession(kioskSession);
				this.stopPairPolling();
				this.setState({
					isAuthenticated: true,
					pairing: false,
					kioskSession,
					deviceToken: deviceTokenFinal,
					error: '',
				});
			}
		} catch (e) {
			// Keep polling; transient errors should not clear the code.
			const message = this.getApiErrorMessage(e, '');
			if (message) {
				this.setState({error: message});
			}
		}
	}

	refreshKioskDisplay = async () => {
		const kioskSession = this.state.kioskSession;
		const kioskPkey = kioskSession && kioskSession.pkey;
		const deviceToken = this.getDeviceToken(kioskSession);
		if (!this.state.isAuthenticated || !kioskPkey || !deviceToken || this.state.loading) {
			return;
		}

		try {
			const data = await $.ajax({
				type: 'POST',
				url: kioskApiUrl(`/kiosks/${kioskPkey}/refresh_display`),
				data: {
					object: JSON.stringify({
						pkey: kioskPkey,
						api_key: deviceToken,
					}),
				},
				dataType: 'json',
			});
			const payload = this.unwrapResponse(data);
			if (!payload || !Array.isArray(payload.contentBlocks)) {
				return;
			}

			this.setState((prevState) => {
				const prevBlocks = prevState.kioskSession && prevState.kioskSession.contentBlocks;
				// Avoid rewriting identical blocks every poll — remounts/reset carousel state.
				if (JSON.stringify(prevBlocks) === JSON.stringify(payload.contentBlocks)
					&& (payload.name || prevState.kioskSession.name) === prevState.kioskSession.name
					&& (payload.pkey || prevState.kioskSession.pkey) === prevState.kioskSession.pkey
				) {
					return null;
				}
				const nextSession = {
					...prevState.kioskSession,
					contentBlocks: payload.contentBlocks,
					name: payload.name || prevState.kioskSession.name,
					pkey: payload.pkey || prevState.kioskSession.pkey,
				};
				writeStoredPairSession(nextSession);
				return {kioskSession: nextSession};
			});
		} catch (e) {
			const message = this.getApiErrorMessage(e, '');
			if (message && /invalid or expired kiosk session/i.test(message)) {
				this.endKioskSession();
				this.setState({error: message});
			}
		}
	}

	getApiContentBlocks = () => {
		const kioskSession = this.state.kioskSession || {};
		const rawBlocks = kioskSession.contentBlocks || kioskSession.blocks || kioskSession.modules || kioskSession.content || [];
		if (!Array.isArray(rawBlocks)) {
			return [];
		}
		return rawBlocks;
	}

	isCheckoutShowing = (contentBlocks) => {
		const blocks = contentBlocks || this.getApiContentBlocks();
		return blocks.some((block) => {
			if (!block || block.type !== 'checkout') {
				return false;
			}
			const items = block.config && block.config.items;
			return Array.isArray(items) && items.length > 0;
		});
	}

	renderExitHoldControl() {
		const progress = this.state.exitHoldProgress || 0;
		return (
			<div
				onMouseDown={this.beginExitHold}
				onTouchStart={this.beginExitHold}
				onMouseUp={this.clearExitHold}
				onMouseLeave={this.clearExitHold}
				onTouchEnd={this.clearExitHold}
				onTouchCancel={this.clearExitHold}
				style={{
					position: 'fixed',
					top: 0,
					right: 0,
					width: 72,
					height: 72,
					zIndex: 20,
					cursor: 'default',
					touchAction: 'none',
					WebkitUserSelect: 'none',
					userSelect: 'none',
				}}
			>
				{progress > 0 ? (
					<div
						style={{
							position: 'absolute',
							top: 8,
							right: 8,
							width: 28,
							height: 28,
							borderRadius: '50%',
							border: '2px solid rgba(0,0,0,0.15)',
							background: `conic-gradient(#666 ${Math.round(progress * 360)}deg, transparent 0deg)`,
							opacity: 0.55,
						}}
					/>
				) : null}
			</div>
		);
	}

	formatPairCode = (code) => {
		const digits = String(code || '').replace(/\D/g, '').padStart(6, '0').slice(0, 6);
		return `${digits.slice(0, 3)} ${digits.slice(3)}`;
	}

	renderPairing() {
		const expiresAt = this.state.pairExpiresAt ? Number(this.state.pairExpiresAt) * 1000 : null;
		const secondsLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)) : null;

		return (
			<div className="body-inner">
				<Card>
					<CardContent>
						<section className="logo text-center">
							<h1><a href="/">{this.brand}</a></h1>
						</section>
						<h4 style={{marginTop: 0, textAlign: 'center'}}>Pair this display</h4>
						<p style={{textAlign: 'center', color: '#555', marginBottom: '20px'}}>
							In the POS, open the kiosk you want to use and enter this code.
						</p>
						<div
							style={{
								textAlign: 'center',
								fontSize: '3rem',
								letterSpacing: '0.2em',
								fontWeight: 700,
								fontVariantNumeric: 'tabular-nums',
								margin: '12px 0 8px',
							}}
						>
							{this.state.loading && !this.state.pairCode
								? '······'
								: this.formatPairCode(this.state.pairCode)}
						</div>
						{secondsLeft !== null ? (
							<p style={{textAlign: 'center', color: '#777', fontSize: '0.9rem'}}>
								Code refreshes in about {Math.ceil(secondsLeft / 60)} min if unused
							</p>
						) : null}
						{this.state.error ? (
							<div className="errorColor" style={{textAlign: 'center', marginTop: '12px'}}>
								<i>{this.state.error}</i>
							</div>
						) : (
							<p style={{textAlign: 'center', color: '#666', marginTop: '16px'}}>
								Waiting for approval…
							</p>
						)}
					</CardContent>
					<CardActions className={"d-flex justify-content-end mr-2"}>
						<Link to="/login" style={{marginRight: '10px'}}>
							<Button type="button" className="secondary-button" size={"md"} block={false}>Back</Button>
						</Link>
						<Button className="primary-button"
							type="button"
							size={"md"}
							block={false}
							disabled={this.state.loading}
							onClick={this.beginPairRequest}
						>
							{this.state.loading ? 'Working…' : 'New Code'}
						</Button>
					</CardActions>
				</Card>
			</div>
		);
	}

	renderKiosk() {
		const contentBlocks = this.getApiContentBlocks();
		const checkoutShowing = this.isCheckoutShowing(contentBlocks);

		return (
			<div className="body-inner" style={{paddingTop: 0}}>
				{this.renderExitHoldControl()}
				<Card>
					<CardContent style={{paddingTop: checkoutShowing ? '8px' : '12px'}}>
						{!checkoutShowing ? (
							<div className="d-flex justify-content-between align-items-center">
								<h3 style={{margin: 0}}>Kiosk Display</h3>
							</div>
						) : null}
						<div style={{marginTop: checkoutShowing ? 0 : '12px'}}>
							<KioskSurface contentBlocks={contentBlocks} kioskSession={this.state.kioskSession}/>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	render() {
		if (this.state.isAuthenticated) {
			return (
				<div className="page-kiosk" style={{padding: '0 10px'}}>
					<div
						className="main-body"
						style={{
							maxWidth: '960px',
							margin: '0 auto',
							paddingTop: '16px',
						}}
					>
						{this.renderKiosk()}
					</div>
				</div>
			);
		}

		return (
			<div className="page-login">
				<div className="main-body">
					{this.renderPairing()}
				</div>
			</div>
		);
	}
}

const KioskRoute = withRouter(Kiosk);
const Page = () => (
	<div key="1">
		<KioskRoute/>
	</div>
);

export default Page;
